import { useState, useMemo } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Table2 } from 'lucide-react';
import ExportButton from './ExportButton';
import styles from './DataTable.module.css';

function DataTable({
    data = [],
    columns = [],
    title = 'Data Table',
    icon: Icon = Table2,
    searchable = true,
    sortable = true,
    exportable = true,
    exportFilename = 'export',
    exportData,
    exportColumns,
    pageSizes = [10, 25, 50, 100],
    defaultPageSize = 10,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    // Filter data by search
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return data;

        const query = searchQuery.toLowerCase();
        return data.filter(row =>
            columns.some(col => {
                const value = row[col.key];
                return value && String(value).toLowerCase().includes(query);
            })
        );
    }, [data, searchQuery, columns]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (aVal === bVal) return 0;

            const comparison = aVal < bVal ? -1 : 1;
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [filteredData, sortConfig]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sortedData.slice(startIndex, startIndex + pageSize);
    }, [sortedData, currentPage, pageSize]);

    const totalPages = Math.ceil(sortedData.length / pageSize);

    // Handle sort
    const handleSort = (key) => {
        if (!sortable) return;

        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Handle page size change
    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    // Render cell value
    const renderCell = (row, column) => {
        const value = row[column.key];

        if (column.render) {
            return column.render(value, row);
        }

        return value;
    };

    // Render payment chip
    const renderPaymentChip = (method) => {
        const chipClass = method === 'Online' ? styles.online : styles.cod;
        return <span className={`${styles.chip} ${chipClass}`}>{method}</span>;
    };

    return (
        <div className={styles.tableContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <Icon size={20} className={styles.icon} />
                    <div>
                        <h3 className={styles.title}>{title}</h3>
                        <p className={styles.recordCount}>{sortedData.length} records</p>
                    </div>
                </div>

                <div className={styles.actions}>
                    {searchable && (
                        <div className={styles.searchWrapper}>
                            <Search size={16} className={styles.searchIcon} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    )}

                    {exportable && (
                        <ExportButton
                            data={typeof exportData === 'function' ? exportData(sortedData) : (exportData || sortedData)}
                            filename={exportFilename}
                            columns={exportColumns || columns.map(c => c.key)}
                        />
                    )}
                </div>
            </div>

            {/* Desktop Table */}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {columns.map(column => (
                                <th
                                    key={column.key}
                                    className={sortConfig.key === column.key ? styles.sorted : ''}
                                    onClick={() => handleSort(column.key)}
                                    style={{ width: column.width }}
                                >
                                    {column.label}
                                    {sortable && (
                                        <span className={styles.sortIcon}>
                                            {sortConfig.key === column.key ? (
                                                sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                                            ) : (
                                                <ChevronDown size={14} />
                                            )}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => (
                                <tr key={row.id || index}>
                                    {columns.map(column => (
                                        <td key={column.key}>
                                            {column.key === 'paymentMethod'
                                                ? renderPaymentChip(row[column.key])
                                                : renderCell(row, column)
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className={styles.emptyState}>
                                    No data found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className={styles.mobileCards}>
                {paginatedData.map((row, index) => (
                    <div key={row.id || index} className={styles.mobileCard}>
                        {columns.map(column => (
                            <div key={column.key} className={styles.mobileCardRow}>
                                <span className={styles.mobileCardLabel}>{column.label}</span>
                                <span className={styles.mobileCardValue}>
                                    {column.key === 'paymentMethod'
                                        ? renderPaymentChip(row[column.key])
                                        : renderCell(row, column)
                                    }
                                </span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* Footer */}
            {sortedData.length > 0 && (
                <div className={styles.footer}>
                    <span className={styles.pageInfo}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length}
                    </span>

                    <div className={styles.paginationControls}>
                        <select
                            className={styles.pageSizeSelect}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            {pageSizes.map(size => (
                                <option key={size} value={size}>{size} / page</option>
                            ))}
                        </select>

                        <button
                            className={`${styles.pageBtn} ${styles.textBtn}`}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            <ChevronLeft size={16} />
                            Previous
                        </button>

                        <span className={`${styles.pageBtn} ${styles.active}`}>
                            {currentPage}
                        </span>

                        <button
                            className={`${styles.pageBtn} ${styles.textBtn}`}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DataTable;
